import React, { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import API from "../Common/API"
import Button from "./Button"

const REASONS = [
    "Fake profile",
    "Inappropriate behaviour",
    "Harassment",
    "Spam / promotion",
    "Underage / married",
    "Other",
]

const formatName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.refId || "this member"

const ReportUserDialog = ({ open, target, onClose, onReported }) => {
    const [reason, setReason] = useState(REASONS[0])
    const [details, setDetails] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const [submittedId, setSubmittedId] = useState(null)

    if (!open || !target) return null

    const close = () => {
        setReason(REASONS[0])
        setDetails("")
        setError(null)
        setSubmittedId(null)
        onClose?.()
    }

    const submit = async () => {
        if (!target?._id) return
        setSubmitting(true)
        setError(null)
        try {
            const res = await API.post("/reports/create", {
                reportedUserId: target._id,
                reason,
                details: details.trim().slice(0, 1000),
            })
            setSubmittedId(res.data?.data?._id || true)
            onReported?.(target)
        } catch (err) {
            setError(err?.response?.data?.error || err?.message || "Could not submit the report")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                onClick={close}
            >
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                >
                    {!submittedId ? (
                        <>
                            <div className="mb-3 flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#a03e3e]">Report</p>
                                    <h3 className="mt-1 text-xl font-semibold text-tColor">Report {formatName(target)}</h3>
                                    <p className="mt-1 text-sm text-tpColor">
                                        Reports are reviewed by the admin team. The reported member will not be notified.
                                        We will also block this profile from your account.
                                    </p>
                                </div>
                                <button onClick={close} className="text-2xl text-tpColor hover:text-tColor">×</button>
                            </div>

                            <div className="mt-3 space-y-2">
                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-tpColor">Reason</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {REASONS.map((r) => (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => setReason(r)}
                                            className={`rounded-full border px-3 py-2 text-xs font-bold transition ${
                                                reason === r
                                                    ? "border-primeColor bg-primeColor text-white"
                                                    : "border-borderColor bg-white text-tColor hover:border-primeColor hover:text-primeColor"
                                            }`}
                                        >
                                            {r}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="text-xs font-bold uppercase tracking-[0.16em] text-tpColor">Details (optional)</label>
                                <textarea
                                    value={details}
                                    onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
                                    rows={4}
                                    placeholder="Anything specific the admin team should know? (max 1000 chars)"
                                    className="mt-1 w-full rounded-xl border border-borderColor bg-white p-3 text-sm outline-none focus:border-primeColor"
                                />
                                <p className="mt-1 text-right text-[11px] text-tpColor">{details.length} / 1000</p>
                            </div>

                            {error && (
                                <p className="mt-3 rounded-md bg-[#fff5f5] p-3 text-sm text-[#a03e3e]">{error}</p>
                            )}

                            <div className="mt-5 flex justify-end gap-2">
                                <Button type="secondary" onClick={close}>Cancel</Button>
                                <Button type="primary" disabled={submitting} onClick={submit}>
                                    {submitting ? "Submitting..." : "Submit Report"}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#ecf8ef] text-2xl">✓</div>
                            <h3 className="text-xl font-semibold text-tColor">Report received</h3>
                            <p className="mt-2 text-sm text-tpColor">
                                Thank you. Our admin team will review {formatName(target)}'s profile.
                                We've also added them to your block list so they won't appear in your suggestions.
                            </p>
                            <Button type="primary" injectClass="mt-5" onClick={close}>Close</Button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

export default ReportUserDialog
